import convert from "convert-units";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Avatar from "../../../../parts/avatar";
import PaginationState from "../../../../parts/pagination-state";
import SvgIcon from "../../../../parts/SvgIcon";
import { useRillContext } from "../../../../rill/RillReact/share/base";
import getAvatarUrl from "../../../../utils/get-avatar-url";
import { getFullName } from "../../../../utils/personal-records";
import {
  getRule,
  measurementRuleKey,
  repMaxRuleKey,
  windowBadge,
} from "../leaderboard-rules";
import ProgramFilterModal from "./ProgramFilterModal";

const PAGE_LIMIT = 25;

// ---------------------------------------------------------------------------
// Default / fallback column selection — matches the previous hardcoded columns
// (waist and hips were rendered as body measurements previously, so they are
// included in DEFAULT_MEASUREMENTS for backward-compat)
// ---------------------------------------------------------------------------
const DEFAULT_COLUMNS = new Set([
  "workouts",
  "programTeam",
  "completionRate",
  "avgDailySteps",
  "habitsAdherence",
  "bodyweight",
  "photos",
]);

const DEFAULT_MEASUREMENTS = new Set(["waist", "hips"]);

// ---------------------------------------------------------------------------
// Fixed display order for toggleable main columns
// ---------------------------------------------------------------------------
const COLUMN_ORDER = [
  "workouts",
  "programTeam",
  "completionRate",
  "avgDailySteps",
  "habitsAdherence",
  "totalVolume",
  "totalReps",
  "totalSets",
  "avgSessionRpe",
  "avgTrainingReadiness",
  "avgWaterIntake",
  "bodyweight",
  "photos",
];

// Fixed display order for body measurement columns
const MEASUREMENT_ORDER = [
  "neck",
  "chest",
  "leftArm",
  "rightArm",
  "waist",
  "hips",
  "leftThigh",
  "rightThigh",
  "leftCalf",
  "rightCalf",
  "glutes",
];

// ---------------------------------------------------------------------------
// Column definitions
// Each entry: { key, labelKey, sortKey, renderHeader(t, handleSort), render(p, ctx) }
// ---------------------------------------------------------------------------

// Helper: renders a sortable <th> — used by column defs
// `badge`: the column's pinned member-board window ("30D"), if any.
const SortHeaderEl = ({ label, sortKey, handleSort, badge }) => (
  <th>
    {label}{" "}
    <SvgIcon
      src="/img/svg/up_down_arrows.svg"
      onClickFn={() => handleSort(sortKey)}
    />
    {badge && <span className="challenge-report__col-window">{badge}</span>}
  </th>
);

const COLUMN_DEFS = {
  workouts: {
    key: "workouts",
    renderHeader: (t, handleSort, badge) => (
      <SortHeaderEl
        badge={badge}
        key="workouts"
        label={t("challengeReport.table.workouts")}
        sortKey="totalWorkoutDays"
        handleSort={handleSort}
      />
    ),
    render: (p) => (
      <td key="workouts">
        {p.totalScheduledDays
          ? `${p.totalWorkoutDays}/${p.totalScheduledDays}`
          : (p.totalWorkoutDays ?? "-")}
      </td>
    ),
  },

  programTeam: {
    key: "programTeam",
    renderHeader: (t, handleSort, badge) => (
      <SortHeaderEl
        badge={badge}
        key="programTeam"
        label={t("challengeReport.table.programTeam")}
        sortKey="programTeam"
        handleSort={handleSort}
      />
    ),
    render: (p) => <td key="programTeam">{p.programTeam || "-"}</td>,
  },

  completionRate: {
    key: "completionRate",
    renderHeader: (t, handleSort, badge) => (
      <SortHeaderEl
        badge={badge}
        key="completionRate"
        label={t("challengeReport.table.completionRate", "Completion %")}
        sortKey="completionRate"
        handleSort={handleSort}
      />
    ),
    render: (p) => {
      const value =
        p.totalScheduledDays === 0 || p.totalScheduledDays == null
          ? "—"
          : `${Math.round((p.totalWorkoutDays / p.totalScheduledDays) * 100)}%`;
      return <td key="completionRate">{value}</td>;
    },
  },

  avgDailySteps: {
    key: "avgDailySteps",
    renderHeader: (t, handleSort, badge) => (
      <SortHeaderEl
        badge={badge}
        key="avgDailySteps"
        label={t("challengeReport.table.avgSteps")}
        sortKey="avgDailySteps"
        handleSort={handleSort}
      />
    ),
    render: (p) => (
      <td key="avgDailySteps">
        {p.avgDailySteps == null ? "-" : p.avgDailySteps.toLocaleString()}
      </td>
    ),
  },

  habitsAdherence: {
    key: "habitsAdherence",
    renderHeader: (t, handleSort, badge) => (
      <SortHeaderEl
        badge={badge}
        key="habitsAdherence"
        label={t("challengeReport.table.habitsPercent")}
        sortKey="habitsAdherence"
        handleSort={handleSort}
      />
    ),
    render: (p) => (
      <td key="habitsAdherence">
        {p.habitsAdherence == null ? "-" : `${p.habitsAdherence.toFixed(1)}%`}
      </td>
    ),
  },

  totalVolume: {
    key: "totalVolume",
    renderHeader: (t, handleSort, badge) => (
      <SortHeaderEl
        badge={badge}
        key="totalVolume"
        label={t("challengeReport.table.totalVolume", "Total volume")}
        sortKey="totalVolume"
        handleSort={handleSort}
      />
    ),
    render: (p, { units }) => {
      const value =
        p.totalVolume == null
          ? null
          : Math.round(
              units === "imperial" ? p.totalVolume * 2.20462 : p.totalVolume
            );
      return (
        <td key="totalVolume">
          {value == null
            ? "—"
            : `${value.toLocaleString()} ${units === "imperial" ? "lb" : "kg"}`}
        </td>
      );
    },
  },

  totalReps: {
    key: "totalReps",
    renderHeader: (t, handleSort, badge) => (
      <SortHeaderEl
        badge={badge}
        key="totalReps"
        label={t("challengeReport.table.totalReps", "Total reps")}
        sortKey="totalReps"
        handleSort={handleSort}
      />
    ),
    render: (p) => (
      <td key="totalReps">
        {p.totalReps == null ? "-" : p.totalReps.toLocaleString()}
      </td>
    ),
  },

  totalSets: {
    key: "totalSets",
    renderHeader: (t, handleSort, badge) => (
      <SortHeaderEl
        badge={badge}
        key="totalSets"
        label={t("challengeReport.table.totalSets", "Total sets")}
        sortKey="totalSets"
        handleSort={handleSort}
      />
    ),
    render: (p) => (
      <td key="totalSets">
        {p.totalSets == null ? "-" : p.totalSets.toLocaleString()}
      </td>
    ),
  },

  avgSessionRpe: {
    key: "avgSessionRpe",
    renderHeader: (t, handleSort, badge) => (
      <SortHeaderEl
        badge={badge}
        key="avgSessionRpe"
        label={t("challengeReport.table.avgSessionRpe", "Avg session RPE")}
        sortKey="avgSessionRpe"
        handleSort={handleSort}
      />
    ),
    render: (p) => (
      <td key="avgSessionRpe">
        {p.avgSessionRpe == null ? "-" : p.avgSessionRpe.toFixed(1)}
      </td>
    ),
  },

  avgTrainingReadiness: {
    key: "avgTrainingReadiness",
    renderHeader: (t, handleSort, badge) => (
      <SortHeaderEl
        badge={badge}
        key="avgTrainingReadiness"
        label={t(
          "challengeReport.table.avgTrainingReadiness",
          "Avg training readiness"
        )}
        sortKey="avgTrainingReadiness"
        handleSort={handleSort}
      />
    ),
    render: (p) => (
      <td key="avgTrainingReadiness">
        {p.avgTrainingReadiness == null
          ? "-"
          : p.avgTrainingReadiness.toFixed(1)}
      </td>
    ),
  },

  avgWaterIntake: {
    key: "avgWaterIntake",
    renderHeader: (t, handleSort, badge) => (
      <SortHeaderEl
        badge={badge}
        key="avgWaterIntake"
        label={t("challengeReport.table.avgWaterIntake", "Avg water intake")}
        sortKey="avgWaterIntake"
        handleSort={handleSort}
      />
    ),
    render: (p) => (
      <td key="avgWaterIntake">
        {p.avgWaterIntake == null
          ? "-"
          : `${(p.avgWaterIntake / 1000).toFixed(2)} L`}
      </td>
    ),
  },

  bodyweight: {
    key: "bodyweight",
    renderHeader: (t, handleSort, badge) => (
      <SortHeaderEl
        badge={badge}
        key="bodyweight"
        label={t("challengeReport.table.bwChange")}
        sortKey="bodyweight.change"
        handleSort={handleSort}
      />
    ),
    render: (p, { units }) => (
      <td key="bodyweight">
        <DeltaCell
          measurement={p.bodyweight}
          unit={units === "imperial" ? "lb" : "kg"}
          convert={(v) =>
            units === "imperial"
              ? Number.parseFloat(convert(v).from("kg").to("lb").toFixed(1))
              : v
          }
        />
      </td>
    ),
  },

  photos: {
    key: "photos",
    renderHeader: (t) => (
      <th key="photos" className="text-center">
        {t("challengeReport.table.photos")}
      </th>
    ),
    render: (p, { onViewPhotos, t, hasPhotos, hasAllPhotos }) => (
      <td key="photos" className="text-center">
        {hasPhotos(p) ? (
          <div className="challenge-report__photos-actions">
            <button
              data-button-type="secondary"
              className="challenge-report__photos-btn"
              onClick={() => onViewPhotos(p)}
              type="button"
            >
              {t("challengeReport.table.view")}
            </button>
            {hasAllPhotos(p) && (
              <button
                className="challenge-report__compare-chip"
                onClick={() => onViewPhotos(p)}
                type="button"
              >
                <svg
                  viewBox="0 0 16 16"
                  fill="currentColor"
                  width="12"
                  height="12"
                  aria-hidden="true"
                >
                  <path d="M1 4.5h10.086l-1.793-1.793.707-.707L13.414 5.5l-3.414 3.5-.707-.707L11.086 6.5H1V4.5zm14 7H4.914l1.793-1.793-.707-.707L2.586 12.5 6 16l.707-.707L4.914 13.5H15v-2z" />
                </svg>
                {t("challengeReport.table.compare")}
              </button>
            )}
          </div>
        ) : (
          <span className="challenge-report__no-photos">-</span>
        )}
      </td>
    ),
  },
};

const stickyStyle = {
  position: "sticky",
  left: 0,
  zIndex: 1,
  minWidth: "14rem",
};

// Human-readable labels for body measurement columns
export const MEASUREMENT_LABELS = {
  neck: "Neck",
  chest: "Chest",
  leftArm: "Left arm",
  rightArm: "Right arm",
  waist: "Waist",
  hips: "Hips",
  leftThigh: "Left thigh",
  rightThigh: "Right thigh",
  leftCalf: "Left calf",
  rightCalf: "Right calf",
  glutes: "Glutes",
};

// Sort keys for body measurement columns
const MEASUREMENT_SORT_KEYS = {
  neck: "neck.change",
  chest: "chest.change",
  leftArm: "leftArm.change",
  rightArm: "rightArm.change",
  waist: "waist.change",
  hips: "hips.change",
  leftThigh: "leftThigh.change",
  rightThigh: "rightThigh.change",
  leftCalf: "leftCalf.change",
  rightCalf: "rightCalf.change",
  glutes: "glutes.change",
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const ChallengeTable = ({
  participants,
  handleSort,
  search,
  setSearch,
  csvString,
  onViewPhotos,
  programOptions = [],
  selectedPrograms,
  setSelectedPrograms,
  selectedColumns,
  selectedMeasurements,
  activeRepMaxExercises = [],
  columnRules = [],
}) => {
  const { t } = useTranslation("coaching-portal");
  const {
    locals: { activeUser },
  } = useRillContext();
  const units = activeUser.units || "metric";
  const [currentPage, setCurrentPage] = useState(1);

  // Fall back to defaults when props are not supplied
  const activeColumns = selectedColumns ?? DEFAULT_COLUMNS;
  const activeMeasurements = selectedMeasurements ?? DEFAULT_MEASUREMENTS;

  const paginatedParticipants = useMemo(() => {
    const start = (currentPage - 1) * PAGE_LIMIT;
    return participants.slice(start, start + PAGE_LIMIT);
  }, [participants, currentPage]);

  const onPageChanged = (data) => {
    setCurrentPage(data.currentPage);
  };

  // Stable-ordered list of active toggleable column keys
  const activeColumnKeys = COLUMN_ORDER.filter((key) => activeColumns.has(key));

  // Stable-ordered list of active measurement keys
  const activeMeasurementKeys = MEASUREMENT_ORDER.filter((key) =>
    activeMeasurements.has(key)
  );

  const hasPhotos = (p) =>
    p.bodyPhotos && (p.bodyPhotos.before || p.bodyPhotos.after);

  const SIDES = ["front", "side", "back"];
  const hasAllPhotos = (p) => {
    const { before, after } = p.bodyPhotos || {};
    return before && after && SIDES.every((s) => before[s] && after[s]);
  };

  // Shared render context passed to column render fns
  const ctx = { onViewPhotos, t, units, hasPhotos, hasAllPhotos };

  return (
    <>
      <header className="table-header">
        <hgroup>
          <input
            type="search"
            className="text-input"
            name="search"
            placeholder={t("challengeReport.table.searchPlaceholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-expandable="true"
          />
          {programOptions.length > 0 && (
            <ProgramFilterModal
              programOptions={programOptions}
              selectedPrograms={selectedPrograms}
              setSelectedPrograms={setSelectedPrograms}
            />
          )}
        </hgroup>
        {csvString && (
          <a
            role="button"
            target="_blank"
            href={`data:attachment/csv,${encodeURIComponent(csvString)}`}
            download="challenge-report.csv"
            data-button-type="primary"
          >
            {t("challengeReport.table.exportCsv")}
          </a>
        )}
      </header>

      <section role="table-wrapper" data-borders-inside>
        <div style={{ overflowX: "auto", borderRadius: "1rem" }}>
          <table role="report-table" className="challenge-report__table">
            <thead role="table-header">
              <tr>
                {/* Always-on sticky participant column */}
                <th style={stickyStyle}>
                  {t("challengeReport.table.participant")}{" "}
                  <SvgIcon
                    src="/img/svg/up_down_arrows.svg"
                    onClickFn={() => handleSort("name")}
                  />
                </th>

                {/* Toggleable main columns */}
                {activeColumnKeys.map((key) => (
                  <React.Fragment key={key}>
                    {COLUMN_DEFS[key].renderHeader(
                      t,
                      handleSort,
                      windowBadge(getRule(columnRules, key).window)
                    )}
                  </React.Fragment>
                ))}

                {/* Body measurement columns */}
                {activeMeasurementKeys.map((key) => (
                  <SortHeaderEl
                    key={key}
                    badge={windowBadge(
                      getRule(columnRules, measurementRuleKey(key)).window
                    )}
                    label={MEASUREMENT_LABELS[key]}
                    sortKey={MEASUREMENT_SORT_KEYS[key]}
                    handleSort={handleSort}
                  />
                ))}

                {/* Rep-max columns */}
                {activeRepMaxExercises.map((exercise) => {
                  const fn = getFullName(exercise);
                  const colLabel = `${exercise.label || fn} (${exercise.repTarget}RM)`;
                  const sortKey = `repMax_${exercise.repTarget}_${fn}`;
                  return (
                    <SortHeaderEl
                      key={sortKey}
                      badge={windowBadge(
                        getRule(columnRules, repMaxRuleKey(exercise)).window
                      )}
                      label={colLabel}
                      sortKey={sortKey}
                      handleSort={handleSort}
                    />
                  );
                })}
              </tr>
            </thead>

            {paginatedParticipants.length > 0 ? (
              <tbody>
                {paginatedParticipants.map((p) => (
                  <tr key={p._id}>
                    {/* Always-on sticky participant cell */}
                    <td
                      className="challenge-table__sticky-col bg-(--white-05-over-bg-solid)"
                      style={stickyStyle}
                    >
                      <div className="flex items-center gap-2">
                        <div className="image-wrapper shrink-0">
                          <Avatar
                            size="32"
                            url={getAvatarUrl({
                              avatar: { s3Path: p.avatar },
                            })}
                            user={p}
                          />
                        </div>
                        <div className="break-all">
                          <span className="subscriber-text">{p.name}</span>
                          <span className="challenge-report__email">
                            {p.email}
                          </span>
                        </div>
                      </div>
                    </td>

                    {/* Toggleable main columns */}
                    {activeColumnKeys.map((key) => (
                      <React.Fragment key={key}>
                        {COLUMN_DEFS[key].render(p, ctx)}
                      </React.Fragment>
                    ))}

                    {/* Body measurement columns */}
                    {activeMeasurementKeys.map((key) => (
                      <td key={key}>
                        <DeltaCell
                          measurement={p.bodyMeasurements?.[key]}
                          unit="cm"
                        />
                      </td>
                    ))}

                    {/* Rep-max columns */}
                    {activeRepMaxExercises.map((exercise) => {
                      const fn = getFullName(exercise);
                      const data = p.repMax?.[fn]?.[exercise.repTarget];
                      const sortKey = `repMax_${exercise.repTarget}_${fn}`;
                      return (
                        <td key={sortKey}>
                          <DeltaCell
                            measurement={
                              data
                                ? {
                                    before: data.before,
                                    after: data.after,
                                    change: data.gain,
                                  }
                                : null
                            }
                            unit={units === "imperial" ? "lb" : "kg"}
                            convert={
                              units === "imperial"
                                ? (v) =>
                                    Number.parseFloat((v * 2.20462).toFixed(1))
                                : undefined
                            }
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            ) : (
              <tbody>
                <tr>
                  <td colSpan="100%">
                    <div className="body-table-empty">
                      {search ? (
                        <>
                          <span>{t("challengeReport.table.noResultsFor")}</span>
                          <p>{search}</p>
                        </>
                      ) : (
                        <>
                          <p>
                            {t("challengeReport.table.noParticipantsTitle")}
                          </p>
                          <span>
                            {t(
                              "challengeReport.table.noParticipantsDescription"
                            )}
                          </span>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              </tbody>
            )}
          </table>
        </div>
      </section>

      {participants.length > PAGE_LIMIT && (
        <div className="pagination-wrapper my-4">
          <PaginationState
            totalRecords={participants.length}
            pageLimit={PAGE_LIMIT}
            pageNeighbours={1}
            onPageChanged={onPageChanged}
          />
        </div>
      )}
    </>
  );
};

const DeltaCell = ({ measurement, unit, convert: convertFn }) => {
  if (measurement?.change == null) return "—";

  const fmt = (v) => {
    if (v == null) return v;
    const converted = convertFn ? convertFn(v) : v;
    return Number.parseFloat(converted.toFixed(1));
  };
  const { before, after } = measurement;
  const change = fmt(measurement.change);
  const isNegative = change < 0;
  const colorClass = isNegative
    ? "challenge-report__delta--positive"
    : change > 0
      ? "challenge-report__delta--negative"
      : "";

  return (
    <div className="challenge-report__delta-cell">
      <span className="challenge-report__delta-range">
        {before == null ? "?" : fmt(before)} &rarr;{" "}
        {after == null ? "?" : fmt(after)}
      </span>
      <span className={`challenge-report__delta ${colorClass}`}>
        {change > 0 ? "+" : ""}
        {change} {unit}
      </span>
    </div>
  );
};

export default ChallengeTable;
